/**
 * 智盾安全助手 — 鉴权工具
 *
 * 职责划分：
 *  - 类型定义（ApiEnvelope / AuthUser / ZdRuntimeConfig）
 *  - 运行时配置加载（public/config.json，nginx 静态提供，无需重建）
 *  - Token / User 的 localStorage + cookie 双写
 *  - 密码传输加密（RSA-OAEP + SHA-256，降级为 plain: 前缀）
 *  - login() 直接 fetch（绕过 zd-request，避免循环依赖）
 *  - 权限判断 hasPermission
 *  - 导航工具：goToLogin / withBasePath / normalizeRedirect
 *
 * 注意：本模块不可导入 zd-request.ts，否则产生循环依赖。
 */

// ─── 类型 ─────────────────────────────────────────────────────────────────────

/** 统一 API 信封。code === 0 表示成功，其余抛错。 */
export type ApiEnvelope<T> = {
  code: number
  message: string
  data: T
  traceId?: string
}

/** 已登录用户信息（与后端字段完全对齐） */
export type AuthUser = {
  id: number
  username: string
  email: string
  roleCode: string
  roleName: string
  dataScope: string
  permissions: string[]
}

/** public/config.json 结构。由 nginx 静态提供，无需重建前端即可修改。 */
export type ZdRuntimeConfig = {
  /**
   * 所有非 auth 接口的基础路径，e.g. "/mep/api"。
   * 用于：GET ${apiBaseUrl}/security/public-key 等。
   */
  apiBaseUrl: string
  /**
   * 认证接口的基础路径，e.g. "/mep/api/auth"。
   * nginx 配置示例：
   *   location /mep { proxy_pass http://login-service; }
   */
  authApiUrl: string
}

// ─── 运行时配置 ───────────────────────────────────────────────────────────────

const DEFAULT_CONFIG: ZdRuntimeConfig = {
  apiBaseUrl: "/mep/api",
  authApiUrl: "/mep/api/auth",
}

let _runtimeConfig: ZdRuntimeConfig | null = null

/**
 * 从 /config.json 加载运行时配置。首次调用发起请求，之后使用缓存。
 * 加载失败时静默降级到默认值。
 */
export async function loadRuntimeConfig(): Promise<ZdRuntimeConfig> {
  if (_runtimeConfig) return _runtimeConfig
  try {
    const res = await fetch("/config.json", { cache: "no-cache" })
    if (res.ok) {
      const json = (await res.json()) as Partial<ZdRuntimeConfig>
      _runtimeConfig = { ...DEFAULT_CONFIG, ...json }
      return _runtimeConfig
    }
  } catch {
    // 静默降级
  }
  _runtimeConfig = { ...DEFAULT_CONFIG }
  return _runtimeConfig
}

/** 同步读取已缓存的配置（未加载时返回默认值）。 */
export function getRuntimeConfig(): ZdRuntimeConfig {
  return _runtimeConfig ?? DEFAULT_CONFIG
}

// ─── 存储 ─────────────────────────────────────────────────────────────────────

const LS_TOKEN_KEY = "zdas_access_token"
const LS_USER_KEY = "zdas_user"
const COOKIE_TOKEN_KEY = "zdas_access_token"

function ls(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null
  } catch {
    return null
  }
}

function writeCookie(value: string): void {
  if (typeof document === "undefined") return
  document.cookie = `${COOKIE_TOKEN_KEY}=${encodeURIComponent(value)}; path=/; max-age=604800; SameSite=Lax`
}

function clearCookie(): void {
  if (typeof document === "undefined") return
  document.cookie = `${COOKIE_TOKEN_KEY}=; path=/; max-age=0; SameSite=Lax`
}

// ─── 用户校验 / 归一化 ────────────────────────────────────────────────────────

/**
 * 对后端返回的 user 对象做字段校验与归一化。
 * 不合法时返回 null（setAuth 会拒绝写入）。
 */
export function validateUser(raw: unknown): AuthUser | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>

  // id：接受 number 或可解析的数字字符串
  const rawId = r["id"]
  const id =
    typeof rawId === "number"
      ? rawId
      : typeof rawId === "string"
        ? parseInt(rawId, 10)
        : NaN
  if (!Number.isFinite(id)) return null

  // 必填字符串字段
  const strFields = ["username", "email", "roleCode", "roleName", "dataScope"] as const
  for (const f of strFields) {
    if (typeof r[f] !== "string" || !(r[f] as string).trim()) return null
  }

  // permissions 必须是字符串数组
  if (!Array.isArray(r["permissions"]) || !(r["permissions"] as unknown[]).every((p) => typeof p === "string")) {
    return null
  }

  return {
    id,
    username: (r["username"] as string).trim(),
    email: (r["email"] as string).trim(),
    roleCode: (r["roleCode"] as string).trim(),
    roleName: (r["roleName"] as string).trim(),
    dataScope: (r["dataScope"] as string).trim(),
    permissions: r["permissions"] as string[],
  }
}

// ─── Token / User 读写 ────────────────────────────────────────────────────────

/** 将 token 和 user 写入 localStorage（+cookie）。user 非法则抛错拒绝写入。 */
export function setAuth(token: string, rawUser: unknown): void {
  const user = validateUser(rawUser)
  if (!user) throw new Error("用户信息格式不合法，登录被拒绝")

  const store = ls()
  if (store) {
    store.setItem(LS_TOKEN_KEY, token)
    store.setItem(LS_USER_KEY, JSON.stringify(user))
  }
  writeCookie(token)
}

/** 清除所有鉴权凭证（localStorage + cookie）。 */
export function clearAuth(): void {
  const store = ls()
  if (store) {
    store.removeItem(LS_TOKEN_KEY)
    store.removeItem(LS_USER_KEY)
  }
  clearCookie()
}

/** 读取 accessToken，不存在返回 null。 */
export function getAccessToken(): string | null {
  return ls()?.getItem(LS_TOKEN_KEY) ?? null
}

/** 读取已存储的用户信息，不存在或解析失败返回 null。 */
export function getCurrentUser(): AuthUser | null {
  try {
    const raw = ls()?.getItem(LS_USER_KEY)
    if (!raw) return null
    return validateUser(JSON.parse(raw))
  } catch {
    return null
  }
}

/** 是否已登录（存在 accessToken）。 */
export function isAuthenticated(): boolean {
  return !!getAccessToken()
}

// ─── 权限判断 ─────────────────────────────────────────────────────────────────

/**
 * 判断用户是否拥有指定权限标识。
 *
 * 规则（按优先级）：
 * 1. `roleCode === 'super_admin'` → 全部通过
 * 2. permissions 包含 `'*'` 或 `'*:*'` → 全部通过
 * 3. permissions 精确包含 key → 通过
 * 4. permissions 中存在 `'a:b:*'` 形式的前缀通配 → key 以 `'a:b:'` 开头则通过
 */
export function hasPermission(user: AuthUser | null, key: string): boolean {
  if (!user) return false
  if (user.roleCode === "super_admin") return true

  const perms = user.permissions
  if (perms.includes("*") || perms.includes("*:*")) return true
  if (perms.includes(key)) return true

  for (const perm of perms) {
    if (!perm.endsWith(":*")) continue
    // "a:b:*" → prefix = "a:b:" → 检查 key.startsWith("a:b:")
    const prefix = perm.slice(0, -1)
    if (key.startsWith(prefix)) return true
  }

  return false
}

// ─── 导航辅助 ─────────────────────────────────────────────────────────────────

/**
 * 拼接 Vite basePath（`import.meta.env.BASE_URL`，默认 "/"）与路径。
 * 用于确保部署在子路径时跳转地址仍正确。
 */
/**
 * 拼接应用基础路径与目标路径。
 *
 * 基础路径来源（按优先级）：
 *  1. HTML 中 <base href="..."> 标签（Vite 构建时可注入）
 *  2. 默认 "/"（nginx 部署通常无子路径）
 */
export function withBasePath(path: string): string {
  const baseEl = typeof document !== "undefined" ? document.querySelector("base") : null
  const base: string = baseEl?.getAttribute("href") ?? "/"
  const trimmedBase = base.endsWith("/") ? base.slice(0, -1) : base
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  return trimmedBase + normalizedPath
}

/**
 * 归一化 redirect 参数，防止开放重定向攻击。
 *
 * 允许：站内相对路径（以 `/` 开头，非 `/auth/*`）
 * 拒绝：`http(s)://…`、`//…`、`/auth/*`、空值
 */
export function normalizeRedirect(redirect: string | null | undefined): string {
  const s = redirect?.trim() ?? ""
  if (!s) return "/"
  if (/^https?:\/\//i.test(s)) return "/"
  if (s.startsWith("//")) return "/"
  if (!s.startsWith("/")) return "/"
  if (/^\/auth(\/|$)/i.test(s)) return "/"
  return s
}

/**
 * 清除凭证并跳转到登录页，可选择附带 redirect 参数以便登录后回跳。
 * 不传 redirectTo 时使用当前路径。
 */
export function goToLogin(redirectTo?: string): void {
  clearAuth()
  const target = redirectTo ?? location.pathname + location.search + location.hash
  const safeRedirect = normalizeRedirect(target)
  const redirectParam = safeRedirect !== "/" ? `?redirect=${encodeURIComponent(safeRedirect)}` : ""
  window.location.href = withBasePath("/") + redirectParam
}

// ─── 密码加密 ─────────────────────────────────────────────────────────────────

/**
 * 从服务端获取 RSA 公钥并用 RSA-OAEP + SHA-256 加密明文密码。
 *
 * 降级策略：当 `crypto.subtle` 不可用（http 非安全上下文）时，
 * 返回 `"plain:<明文>"`，后端需识别该前缀并直接使用明文。
 */
async function encryptPassword(plaintext: string): Promise<string> {
  const config = getRuntimeConfig()

  // 1. 获取公钥（直接 fetch，不经过 zd-request 以避免循环依赖）
  let publicKeyBase64: string
  try {
    const res = await fetch(`${config.apiBaseUrl}/security/public-key`, {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const envelope = (await res.json()) as ApiEnvelope<{ publicKey: string }>
    if (envelope.code !== 0 || !envelope.data?.publicKey) {
      throw new Error(envelope.message || "公钥获取失败")
    }
    publicKeyBase64 = envelope.data.publicKey
  } catch (err) {
    throw new Error(`获取加密公钥失败：${err instanceof Error ? err.message : String(err)}`)
  }

  // 2. 降级：非安全上下文（如 http）中 crypto.subtle 不可用
  if (!window.crypto?.subtle) {
    return `plain:${plaintext}`
  }

  // 3. 解码 Base64 SPKI → ArrayBuffer
  const keyBinary = atob(publicKeyBase64)
  const keyBuffer = new Uint8Array(keyBinary.length)
  for (let i = 0; i < keyBinary.length; i++) {
    keyBuffer[i] = keyBinary.charCodeAt(i)
  }

  // 4. 导入公钥
  const publicKey = await crypto.subtle.importKey(
    "spki",
    keyBuffer.buffer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  )

  // 5. 加密
  const encoded = new TextEncoder().encode(plaintext)
  const encrypted = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, encoded)

  // 6. ArrayBuffer → Base64（用传统索引循环，避免 downlevelIteration 要求）
  const encArr = new Uint8Array(encrypted)
  let binary = ""
  for (let i = 0; i < encArr.length; i++) binary += String.fromCharCode(encArr[i]!)
  return btoa(binary)
}

// ─── 登录 API ─────────────────────────────────────────────────────────────────

/** 登录错误（携带后端 code 以便上层区分业务错误） */
export class LoginError extends Error {
  constructor(
    message: string,
    public readonly code?: number,
  ) {
    super(message)
    this.name = "LoginError"
  }
}

/**
 * 登录。
 *
 * 流程：
 * 1. 从 `/security/public-key` 获取公钥并加密密码（RSA-OAEP）
 * 2. POST `${authApiUrl}/login`
 * 3. 解析信封（code !== 0 抛错）
 * 4. validateUser 校验用户信息
 * 5. setAuth 写入 token + cookie
 * 6. 返回 AuthUser
 *
 * 注意：本函数直接调用 fetch，不经过 zd-request.ts，以避免循环依赖。
 */
export async function login(account: string, password: string): Promise<AuthUser> {
  const config = getRuntimeConfig()

  // 加密密码
  let encryptedPassword: string
  try {
    encryptedPassword = await encryptPassword(password)
  } catch (err) {
    throw new LoginError(err instanceof Error ? err.message : "密码加密失败")
  }

  // 调用登录接口
  let res: Response
  try {
    res = await fetch(`${config.authApiUrl}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ account: account.trim(), password: encryptedPassword }),
      credentials: "include",
      cache: "no-store",
    })
  } catch {
    throw new LoginError("网络请求失败，请检查网络连接")
  }

  // 解析信封
  let envelope: ApiEnvelope<{ accessToken: string; user: unknown }>
  try {
    envelope = (await res.json()) as ApiEnvelope<{ accessToken: string; user: unknown }>
  } catch {
    throw new LoginError(`服务器响应异常 (HTTP ${res.status})`)
  }

  if (envelope.code !== 0) {
    throw new LoginError(envelope.message || `登录失败 (code ${envelope.code})`, envelope.code)
  }

  const { accessToken, user: rawUser } = envelope.data
  if (!accessToken) throw new LoginError("服务器返回的 token 为空")

  // 校验并写入鉴权信息（validateUser 内部已做归一化）
  try {
    setAuth(accessToken, rawUser)
  } catch (err) {
    throw new LoginError(err instanceof Error ? err.message : "用户信息写入失败")
  }

  // 此时 getCurrentUser() 已可用
  return getCurrentUser()!
}
